import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, FileCheck, History, LogOut, ChevronRight, AlertTriangle, CheckCircle2, Clock, Loader2, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApp } from '@/context/AppContext';
import apiService from '@/services/apiService';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, authLoading } = useApp();
  
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, avgScore: 0, passed: 0 });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !location.state?.user) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate, location.state]);

  // Load user's recent analyses
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const data = await apiService.getDocumentHistory();
        const analyses = data.analyses || [];
        setRecentAnalyses(analyses.slice(0, 5)); // Show last 5
        
        // Calculate stats
        if (analyses.length > 0) {
          const avgScore = Math.round(
            analyses.reduce((sum, a) => sum + (a.readiness_score || 0), 0) / analyses.length
          );
          const passed = analyses.filter(a => a.readiness_score >= 70).length;
          setStats({ total: analyses.length, avgScore, passed });
        }
      } catch (error) {
        console.error('Failed to load history:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated || location.state?.user) {
      loadHistory();
    }
  }, [isAuthenticated, location.state]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusColor = (score) => {
    if (score >= 80) return 'text-verify bg-verify/10';
    if (score >= 50) return 'text-warning bg-warning/10';
    return 'text-destructive bg-destructive/10';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Use user from location state if available (just logged in)
  const displayUser = user || location.state?.user;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-trust" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </button>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/history')}
              className="text-sm font-public text-slate-600 hover:text-trust transition-colors inline-flex items-center gap-1"
              data-testid="view-all-history"
            >
              <History className="w-4 h-4" />
              View All History
            </button>
            
            <div className="flex items-center gap-3 pl-4 border-l border-slate-200">
              {displayUser?.picture ? (
                <img src={displayUser.picture} alt={displayUser.name} className="w-8 h-8 rounded-full" />
              ) : (
                <User className="w-6 h-6 text-slate-400" />
              )}
              <span className="font-public text-sm text-slate-700">{displayUser?.name}</span>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-destructive transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-2">
            Welcome back, {displayUser?.name?.split(' ')[0]}!
          </h1>
          <p className="font-public text-lg text-slate-600">
            Track your document analyses and start new checks.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-trust/10 p-2 rounded-lg">
                <FileCheck className="w-5 h-5 text-trust" />
              </div>
              <span className="font-public text-sm text-slate-500">Documents Analyzed</span>
            </div>
            <p className="font-outfit font-bold text-3xl text-slate-900">{stats.total}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white border border-slate-200 rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-verify/10 p-2 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-verify" />
              </div>
              <span className="font-public text-sm text-slate-500">Average Score</span>
            </div>
            <p className="font-outfit font-bold text-3xl text-slate-900">{stats.avgScore}%</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-warning/10 p-2 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <span className="font-public text-sm text-slate-500">Low Risk Docs</span>
            </div>
            <p className="font-outfit font-bold text-3xl text-slate-900">{stats.passed}</p>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-gradient-to-r from-trust/5 to-verify/5 border border-trust/20 rounded-lg p-6 mb-10"
        >
          <h2 className="font-outfit font-bold text-lg text-slate-900 mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/select-country')}
              className="bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-semibold hover:bg-trust/90 active:scale-95 transition-all shadow-sm inline-flex items-center gap-2"
              data-testid="new-analysis-btn"
            >
              <FileCheck className="w-5 h-5" />
              Analyze New Documents
            </button>
            <button
              onClick={() => navigate('/history')}
              className="border border-slate-300 text-slate-700 px-6 py-3 rounded-md font-public font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2"
              data-testid="view-history-btn"
            >
              <History className="w-5 h-5" />
              View Full History
            </button>
          </div>
        </motion.div>

        {/* Recent Analyses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-bold text-xl text-slate-900">Recent Analyses</h2>
            {recentAnalyses.length > 0 && (
              <button
                onClick={() => navigate('/history')}
                className="text-sm text-trust hover:underline inline-flex items-center gap-1"
              >
                View all <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {loading ? (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-trust mx-auto mb-3" />
              <p className="text-slate-500">Loading your history...</p>
            </div>
          ) : recentAnalyses.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
              <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-outfit font-bold text-lg text-slate-900 mb-2">No analyses yet</h3>
              <p className="text-slate-500 mb-6">Start by analyzing your first document</p>
              <button
                onClick={() => navigate('/select-country')}
                className="bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-semibold hover:bg-trust/90 transition-colors"
              >
                Analyze Documents
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentAnalyses.map((analysis, idx) => (
                <motion.div
                  key={analysis.document_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * idx }}
                  onClick={() => navigate('/results', { state: { analysis, isRealData: true, isPaid: true } })}
                  className="bg-white border border-slate-200 rounded-lg p-4 hover:border-trust/50 hover:shadow-sm transition-all cursor-pointer"
                  data-testid={`recent-analysis-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${getStatusColor(analysis.readiness_score)}`}>
                        <FileCheck className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-outfit font-semibold text-slate-900">
                          {analysis.document_type}
                        </h3>
                        <p className="text-sm text-slate-500 flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {formatDate(analysis.created_at)}
                          {analysis.critical_issues?.length > 0 && (
                            <span className="text-destructive">
                              • {analysis.critical_issues.length} issue{analysis.critical_issues.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className={`text-2xl font-bold ${
                        analysis.readiness_score >= 80 ? 'text-verify' :
                        analysis.readiness_score >= 50 ? 'text-warning' : 'text-destructive'
                      }`}>
                        {analysis.readiness_score}%
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default Dashboard;
